import { dayId, XiaoyuPhase, type GameState } from '../core/state';
import { CAST, type CharLook } from '../render/sprites';
import type { Messages } from '../i18n';
import { px, type MapId, type TourKeys } from './mapdef';
import { caseLive, caseToday } from '../content/clsPlan';

export type NpcId = keyof typeof CAST | 'cat' | 'horse' | 'pony'
  // phase E district packages register their own NPCs (see registerNpcs)
  | `xy_${string}` | `xm_${string}` | `ck_${string}` | `hc_${string}` | `tk_${string}`
  | `ap_${string}` | `kh_${string}` | `tn_${string}` | `bc_${string}`;
export type NpcLabel = keyof Messages['npc'] | keyof Messages['places']['npc'] | keyof Messages['world3']['npc'] | keyof Messages['trips']['npc'] | keyof Messages['classroom']['npc'] | keyof Messages['family']['npc'] | TourKeys<'npc'>;

export interface NpcSpot {
  map: MapId;
  x: number;
  y: number;
  /** Wander radius in px (0 = stands still). */
  wander?: number;
}

export interface NpcDef {
  id: NpcId;
  look: CharLook | null;
  label: NpcLabel;
  where(s: GameState): NpcSpot | null;
}

const between = (s: GameState, from: number, to: number) => s.time >= from && s.time < to;
const H = (h: number, m = 0) => h * 60 + m;

type Entry = [from: number, to: number, spot: NpcSpot];
/** First schedule entry that covers the current time. */
const sched = (s: GameState, entries: Entry[]): NpcSpot | null => entries.find(([a, b]) => s.time >= a && s.time < b)?.[2] ?? null;

// Frequently used spots.
const SCHOOL = {
  classroomFront: { map: 'school', x: px(25), y: px(4) + 4 } as NpcSpot,
  cafeA: { map: 'school', x: px(26), y: px(24) + 4, wander: 10 } as NpcSpot,
  cafeB: { map: 'school', x: px(30), y: px(21) + 8, wander: 10 } as NpcSpot,
  cafeC: { map: 'school', x: px(34), y: px(25) + 4, wander: 8 } as NpcSpot,
  library: { map: 'school', x: px(34), y: px(10) + 8 } as NpcSpot,
  libraryDesk: { map: 'school', x: px(41), y: px(10) + 4 } as NpcSpot,
  corridor: { map: 'school', x: px(20), y: px(15), wander: 40 } as NpcSpot,
  classroomDesk: { map: 'school', x: px(18), y: px(8) + 8 } as NpcSpot,
  artRoom: { map: 'school', x: px(6), y: px(24), wander: 24 } as NpcSpot,
};
/** Classmates' spots (classroom package): desks are aisle spots, the rest are case spots. */
const CLASS = {
  door: { map: 'school', x: px(23), y: px(14) + 2 } as NpcSpot,
  corridor: { map: 'school', x: px(13), y: px(15) + 4 } as NpcSpot,
  back: { map: 'school', x: px(27) + 10, y: px(11) + 4 } as NpcSpot,
  yardA: { map: 'town', x: px(38) + 8, y: px(15) + 2 } as NpcSpot,
  yardB: { map: 'town', x: px(46) + 8, y: px(15) + 6 } as NpcSpot,
  deskA: { map: 'school', x: px(21) + 8, y: px(8) + 8 } as NpcSpot,
  deskB: { map: 'school', x: px(24) + 8, y: px(10) + 8 } as NpcSpot,
  deskC: { map: 'school', x: px(21) + 8, y: px(10) + 8 } as NpcSpot,
  deskD: { map: 'school', x: px(24) + 8, y: px(8) + 8 } as NpcSpot,
  deskE: { map: 'school', x: px(18) + 8, y: px(10) + 8 } as NpcSpot,
};
const TOWN = {
  courtyard: { map: 'town', x: px(42), y: px(16), wander: 30 } as NpcSpot,
  gate: { map: 'town', x: px(30) + 8, y: px(20) + 8 } as NpcSpot,
  plaza: { map: 'town', x: px(28), y: px(14), wander: 60 } as NpcSpot,
  field: { map: 'town', x: px(11), y: px(10) + 6, wander: 20 } as NpcSpot,
  parkPath: { map: 'town', x: px(30), y: px(37) + 12, wander: 40 } as NpcSpot,
  picnic: { map: 'town', x: px(45) + 4, y: px(41) + 6 } as NpcSpot,
  pond: { map: 'town', x: px(36), y: px(32) + 8, wander: 12 } as NpcSpot,
  pondWalk: { map: 'town', x: px(44), y: px(36), wander: 70 } as NpcSpot,
  playground: { map: 'town', x: px(23), y: px(39) + 8, wander: 30 } as NpcSpot,
  kiteTree: { map: 'town', x: px(47), y: px(57) + 4, wander: 24 } as NpcSpot,
  storeFront: { map: 'town', x: px(10), y: px(36) + 8, wander: 20 } as NpcSpot,
};
const HOUSES = {
  grandma: { map: 'grandma', x: px(7), y: px(5) + 8, wander: 10 } as NpcSpot,
  bo: { map: 'bo', x: px(5), y: px(7), wander: 14 } as NpcSpot,
  boRoom: { map: 'bo', x: px(10) + 8, y: px(3) + 8, wander: 8 } as NpcSpot,
  boMom: { map: 'bo', x: px(3), y: px(3) + 10, wander: 10 } as NpcSpot,
};
const MARKET_OPEN = [17 * 60, 23 * 60] as const;
const market = (s: GameState, spot: NpcSpot) => (between(s, MARKET_OPEN[0], MARKET_OPEN[1]) ? spot : null);

const HOME = {
  kitchen: { map: 'home', x: px(14) + 4, y: px(4) + 2, wander: 14 } as NpcSpot,
  living: { map: 'home', x: px(13), y: px(12) + 4, wander: 20 } as NpcSpot,
  mat: { map: 'home', x: px(20), y: px(12), wander: 18 } as NpcSpot,
  dadSofa: { map: 'home', x: px(8), y: px(12) + 4, wander: 10 } as NpcSpot,
};

export const NPCS: NpcDef[] = [
  {
    id: 'teacher',
    look: CAST.teacher,
    label: 'teacher',
    where: (s) =>
      dayId(s) !== 'fri' ? null : sched(s, [[H(7), H(12), SCHOOL.classroomFront], [H(12), H(13, 30), SCHOOL.cafeC], [H(13, 30), H(17), SCHOOL.classroomFront]]),
  },
  {
    id: 'xiaoyu',
    look: CAST.xiaoyu,
    label: 'xiaoyu',
    where: (s) => {
      const d = dayId(s);
      if (d === 'fri') {
        const p = s.xiaoyu.phase;
        if (p === XiaoyuPhase.Absent) return null;
        if (p === XiaoyuPhase.Gone) return sched(s, [[H(8), H(12), SCHOOL.classroomDesk], [H(12), H(13, 30), SCHOOL.cafeA], [H(13, 30), H(16), SCHOOL.classroomDesk]]);
        return { map: 'school', x: s.xiaoyu.x, y: s.xiaoyu.y };
      }
      if (d === 'sat' && s.forever.xiaoyuHelped && between(s, H(10), H(18))) {
        return s.quests.xiaoyuPicnic?.done ? { map: 'town', x: px(46) + 8, y: px(42) } : { map: 'town', x: px(36) + 12, y: px(37) };
      }
      return null;
    },
  },
  {
    id: 'chunbo',
    look: CAST.chunbo,
    label: 'chunbo',
    where: (s) => (dayId(s) !== 'fri' ? null : sched(s, [[H(7), H(11), TOWN.gate], [H(11), H(12), TOWN.plaza], [H(12), H(19), TOWN.gate]])),
  },
  {
    id: 'librarian',
    look: CAST.librarian,
    label: 'librarian',
    where: (s) => (dayId(s) === 'fri' && between(s, H(8), H(17)) ? SCHOOL.libraryDesk : null),
  },
  {
    id: 'rain',
    look: CAST.rain,
    label: 'rain',
    where: (s) => {
      if (dayId(s) === 'fri') return sched(s, [[H(8), H(12), SCHOOL.library], [H(12), H(13, 30), SCHOOL.cafeB], [H(13, 30), H(17), SCHOOL.library]]);
      if (dayId(s) === 'sat') return sched(s, [[H(10), H(14), TOWN.playground], [H(14), H(18), TOWN.pondWalk]]);
      return null;
    },
  },
  {
    id: 'coach',
    look: CAST.coach,
    label: 'coach',
    where: (s) => (dayId(s) !== 'fri' ? null : sched(s, [[H(8), H(12), TOWN.field], [H(12), H(13), SCHOOL.cafeA], [H(13), H(17), TOWN.field]])),
  },
  {
    id: 'emma',
    look: CAST.emma,
    label: 'emma',
    where: (s) => {
      const d = dayId(s);
      if (d === 'sat') return sched(s, [[H(8), H(19), TOWN.picnic]]);
      if (d === 'sun') return sched(s, [[H(9), H(13), TOWN.parkPath], [H(13), H(16), TOWN.storeFront], [H(16), H(19), TOWN.pondWalk]]);
      return sched(s, [[H(8), H(10), TOWN.courtyard], [H(10), H(12), SCHOOL.artRoom], [H(12), H(13, 30), SCHOOL.cafeB], [H(13, 30), H(16), TOWN.courtyard], [H(16), H(19), TOWN.parkPath]]);
    },
  },
  {
    id: 'clerk',
    look: CAST.clerk,
    label: 'clerk',
    where: (s) => (between(s, H(7), H(23)) ? { map: 'store', x: px(3), y: px(1) + 14 } : null),
  },
  {
    id: 'grandma',
    look: CAST.grandma,
    label: 'grandma',
    where: (s) => {
      const d = dayId(s);
      if (d === 'fri') return sched(s, [[H(9), H(15), HOUSES.grandma], [H(15), H(18), TOWN.pond], [H(18), H(21), HOUSES.grandma]]);
      return sched(s, [[H(9), H(12), TOWN.pond], [H(12), H(13), HOUSES.grandma], [H(13), H(18), TOWN.pond], [H(18), H(21), HOUSES.grandma]]);
    },
  },
  {
    id: 'bo',
    look: CAST.bo,
    label: 'bo',
    where: (s) =>
      dayId(s) === 'fri'
        ? sched(s, [[H(16), H(18), HOUSES.boRoom], [H(18), H(20), HOUSES.bo]])
        : sched(s, [[H(9), H(12), TOWN.kiteTree], [H(12), H(14), TOWN.playground], [H(14), H(18), TOWN.kiteTree], [H(18), H(20), HOUSES.bo]]),
  },
  {
    id: 'mom',
    look: CAST.mom,
    label: 'mom',
    where: (s) => sched(s, [[0, H(12), HOME.kitchen], [H(12), H(17), HOME.living], [H(17), H(24), HOME.kitchen]]),
  },
  { id: 'sister', look: CAST.sister, label: 'sister', where: () => HOME.mat },
  {
    id: 'dad',
    look: CAST.dad,
    label: 'dad',
    where: (s) => (s.chores.callDad && s.time >= H(18) ? HOME.dadSofa : null),
  },
  { id: 'cat', look: null, label: 'cat', where: (s) => (between(s, H(7), H(21)) ? { map: 'town', x: px(30), y: px(33), wander: 90 } : null) },
  // --- New places ---------------------------------------------------------------------------
  { id: 'nurse', look: CAST.nurse, label: 'nurse', where: (s) => (dayId(s) === 'fri' && between(s, H(8), H(17)) ? { map: 'school', x: px(52) + 8, y: px(10) + 2 } : null) },
  { id: 'musicTeacher', look: CAST.musicTeacher, label: 'musicTeacher', where: (s) => (dayId(s) === 'fri' && between(s, H(8), H(17)) ? { map: 'school', x: px(48), y: px(22), wander: 16 } : null) },
  { id: 'lifeguard', look: CAST.lifeguard, label: 'lifeguard', where: (s) => (between(s, H(9), H(20)) ? { map: 'sports', x: px(18) + 8, y: px(10), wander: 8 } : null) },
  { id: 'barista', look: CAST.barista, label: 'barista', where: (s) => (between(s, H(8), H(21)) ? { map: 'mall', x: px(3), y: px(1) + 14, wander: 6 } : null) },
  { id: 'giftClerk', look: CAST.giftClerk, label: 'giftClerk', where: (s) => (between(s, H(10), H(21)) ? { map: 'mall', x: px(15) + 8, y: px(1) + 14 } : null) },
  { id: 'deptClerk', look: CAST.deptClerk, label: 'deptClerk', where: (s) => (between(s, H(10), H(21)) ? { map: 'mall', x: px(22) + 8, y: px(1) + 14 } : null) },
  { id: 'waiter', look: CAST.waiter, label: 'waiter', where: (s) => (between(s, H(11), H(22)) ? { map: 'mall', x: px(34), y: px(5), wander: 30 } : null) },
  { id: 'stableKeeper', look: CAST.stableKeeper, label: 'stableKeeper', where: (s) => (between(s, H(8), H(18)) ? { map: 'town', x: px(75), y: px(54), wander: 20 } : null) },
  { id: 'horse', look: null, label: 'horse', where: (s) => (between(s, H(7), H(19)) ? { map: 'town', x: px(64), y: px(53), wander: 50 } : null) },
  // --- Homes and the night market -------------------------------------------------------------
  { id: 'boMom', look: CAST.boMom, label: 'boMom', where: (s) => (between(s, H(16), H(21)) ? HOUSES.boMom : null) },
  { id: 'teaVendor', look: CAST.teaVendor, label: 'teaVendor', where: (s) => market(s, { map: 'market', x: px(4), y: px(7) - 2 }) },
  { id: 'chickenVendor', look: CAST.chickenVendor, label: 'chickenVendor', where: (s) => market(s, { map: 'market', x: px(10), y: px(7) - 2 }) },
  { id: 'gameVendor', look: CAST.gameVendor, label: 'gameVendor', where: (s) => market(s, { map: 'market', x: px(25), y: px(9) + 4, wander: 16 }) },
  { id: 'shopper1', look: CAST.shopper1, label: 'shopper1', where: (s) => market(s, { map: 'market', x: px(12), y: px(11), wander: 120 }) },
  { id: 'shopper2', look: CAST.shopper2, label: 'shopper2', where: (s) => market(s, { map: 'market', x: px(24), y: px(12), wander: 110 }) },
  { id: 'shopper3', look: CAST.shopper3, label: 'shopper3', where: (s) => market(s, { map: 'market', x: px(32), y: px(11) + 8, wander: 100 }) },
  // --- Trips ---------------------------------------------------------------------------------
  { id: 'stationClerk', look: CAST.stationClerk, label: 'stationClerk', where: (s) => (between(s, H(6), H(23)) ? { map: 'station', x: px(4), y: px(1) + 12 } : null) },
  { id: 'docent', look: CAST.docent, label: 'docent', where: (s) => (between(s, H(8), H(19)) ? { map: 'museum', x: px(14), y: px(10), wander: 40 } : null) },
  { id: 'receptionist', look: CAST.receptionist, label: 'receptionist', where: () => ({ map: 'hotel', x: px(4), y: px(1) + 12 }) },
  { id: 'pony', look: null, label: 'pony', where: (s) => (between(s, H(7), H(19)) ? { map: 'town', x: px(82), y: px(55), wander: 30 } : null) },
  // --- Classmates (phase D, classroom package) ---------------------------------------------------
  // Fridays at school (their case spot while a case is live), weekends around town.
  {
    id: 'xiaohua',
    look: CAST.xiaohua,
    label: 'xiaohua',
    where: (s) => {
      const d = dayId(s);
      if (d === 'fri') {
        if (caseLive(s, 'late')) return CLASS.door;
        // On a late day he only makes it to his seat after the window.
        if (caseToday(s, 'late') && s.time < H(8, 45)) return null;
        return sched(s, [[H(8), H(12), CLASS.deskA], [H(12), H(13, 30), SCHOOL.cafeA], [H(13, 30), H(16, 30), CLASS.deskA]]);
      }
      if (d === 'sat') return sched(s, [[H(10), H(12), TOWN.playground], [H(14), H(17), TOWN.playground]]);
      return null;
    },
  },
  {
    id: 'xiaomei',
    look: CAST.xiaomei,
    label: 'xiaomei',
    where: (s) => {
      const d = dayId(s);
      if (d === 'fri') {
        if (caseLive(s, 'homework')) return CLASS.corridor;
        return sched(s, [[H(8), H(12), CLASS.deskB], [H(12), H(13, 30), SCHOOL.library], [H(13, 30), H(16, 30), CLASS.deskB]]);
      }
      if (d === 'sun') return sched(s, [[H(10), H(16), { map: 'town', x: px(34), y: px(36) + 8, wander: 10 }]]);
      return null;
    },
  },
  {
    id: 'azhe',
    look: CAST.azhe,
    label: 'azhe',
    where: (s) => {
      const d = dayId(s);
      if (d === 'fri') {
        if (caseLive(s, 'standing')) return CLASS.back;
        return sched(s, [[H(8), H(12), CLASS.deskC], [H(12), H(13, 30), SCHOOL.cafeC], [H(13, 30), H(16, 30), CLASS.deskC]]);
      }
      if (d === 'sun') return sched(s, [[H(11), H(16), TOWN.storeFront]]);
      return null;
    },
  },
  {
    id: 'xiaoqiang',
    look: CAST.xiaoqiang,
    label: 'xiaoqiang',
    where: (s) => {
      const d = dayId(s);
      if (d === 'fri') {
        if (caseLive(s, 'ball')) return CLASS.yardA;
        return sched(s, [[H(8), H(12), CLASS.deskD], [H(12), H(13, 30), { ...TOWN.field, x: TOWN.field.x + 24 }], [H(13, 30), H(16, 30), CLASS.deskD]]);
      }
      if (d === 'sat') return sched(s, [[H(9), H(17), { ...TOWN.field, x: TOWN.field.x + 30 }]]);
      return null;
    },
  },
  {
    id: 'xiaofang',
    look: CAST.xiaofang,
    label: 'xiaofang',
    where: (s) => {
      const d = dayId(s);
      if (d === 'fri') {
        if (caseLive(s, 'ball')) return CLASS.yardB;
        return sched(s, [[H(8), H(12), CLASS.deskE], [H(12), H(13, 30), { ...TOWN.field, x: TOWN.field.x - 20 }], [H(13, 30), H(16, 30), CLASS.deskE]]);
      }
      if (d === 'sun') return sched(s, [[H(9), H(12), { map: 'town', x: px(63), y: px(15), wander: 24 }], [H(14), H(17), TOWN.playground]]);
      return null;
    },
  },
];

/** Feature packages add their NPCs here (ids use the package prefix). */
export function registerNpcs(defs: NpcDef[]): void {
  NPCS.push(...defs);
}

export function npcDef(id: NpcId): NpcDef {
  return NPCS.find((n) => n.id === id)!;
}

/** Xiaoyu's Friday route: corridor → counseling room seat, and back out to class. */
export const XIAOYU_IN = [
  { x: px(7) + 4, y: px(14) + 8 },
  { x: px(7) + 4, y: px(11) },
  { x: px(9) + 6, y: px(9) + 8 },
];
export const XIAOYU_OUT = [
  { x: px(7) + 4, y: px(11) },
  { x: px(7) + 4, y: px(14) + 8 },
];
